import { PreferenceGroupSubTypeEnum } from 'api-hooks/preference-tag';
import { useFormState } from 'components/elements/form';
import preferenceColumn from 'components/elements/table/editable-columns/preference-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetAirlineColumn from 'hooks/use-get-airline-column';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewFlight } from '../../hooks/use-get-default-values';
import {
  VisaDocumentFlightItemFormType,
  VisaDocumentFormType,
} from '../visa-document-form-type';

interface Props {
  parentIndex: number;
}

function useGetVisaFlightTableList(parentIndex: number = 0) {
  const { editable } = useFormState();
  const { t } = useTranslation();

  const airlineColumn = useGetAirlineColumn();

  const columns = React.useMemo<
    IColumn<VisaDocumentFlightItemFormType>[]
  >(() => {
    return [
      {
        header: t('common:airline'),
        accessorKey: 'airlineId',
        minSize: 250,
        dataType: ColumnDataType.select,
        selectProps: airlineColumn,
        required: true,
      },
      {
        header: t('common:class'),
        accessorKey: 'preferenceId',
        minSize: 120,
        dataType: ColumnDataType.select,
        selectProps: preferenceColumn({
          subType: PreferenceGroupSubTypeEnum.FlightClass,
        }),
        required: true,
      },
      {
        header: t('common:route'),
        accessorKey: 'route',
        minSize: 100,
        dataType: ColumnDataType.text,
        required: true,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [airlineColumn, editable, t]);

  return columns;
}

export default function VisaFlightTable(props: Props) {
  const { editable } = useFormState();

  const name = `visaDocumentFlights.${props.parentIndex}.items` as const;

  const { control, trigger, getValues, clearErrors } =
    useFormContext<VisaDocumentFormType>();

  const { fields, update, append, remove } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetVisaFlightTableList();

  const dispatch = React.useCallback(
    (action) => {
      const addons = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const addon = addons[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof VisaDocumentFlightItemFormType;
      const value = action.value;

      const otherVal: () => Partial<VisaDocumentFlightItemFormType> = () => {
        return {};
      };

      if (type === 'addRow') {
        append(addNewFlight());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...addon,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <TableInput
      columns={columns}
      data={fields}
      dispatch={dispatch}
      disabled={!editable}
      name={name}
      uniqueRowKey="customId"
    />
  );
}
